/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { Editor, EditorState, RichUtils, convertToRaw, convertFromRaw, ContentState } from "draft-js";
import "draft-js/dist/Draft.css";

/**
 * Convert Draft.js raw content JSON to inline HTML string.
 * Used for canvas preview rendering.
 */
export function draftRawToHtml(raw) {
  if (!raw || !raw.blocks) return "";

  return raw.blocks
    .map((block) => {
      let text = block.text;
      if (!text) return "<br/>";

      // Build array of characters with their styles
      const chars = text.split("").map((ch, i) => {
        const styles = (block.inlineStyleRanges || []).filter(
          (r) => i >= r.offset && i < r.offset + r.length
        );
        let escaped = ch
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");

        styles.forEach((s) => {
          if (s.style === "BOLD") escaped = `<strong>${escaped}</strong>`;
          if (s.style === "ITALIC") escaped = `<em>${escaped}</em>`;
          if (s.style === "UNDERLINE") escaped = `<u>${escaped}</u>`;
        });

        return escaped;
      });

      // Merge adjacent identical tags
      let html = chars.join("");
      // Clean up adjacent tags: </strong><strong> etc.
      html = html.replace(/<\/strong><strong>/g, "");
      html = html.replace(/<\/em><em>/g, "");
      html = html.replace(/<\/u><u>/g, "");

      return `<p style="margin:0;">${html}</p>`;
    })
    .join("");
}

/**
 * Create EditorState from stored content.
 * Handles: raw JSON object, plain string, or null/undefined.
 */
export function createEditorStateFromContent(content) {
  if (!content) {
    return EditorState.createEmpty();
  }
  if (typeof content === "object" && content.blocks) {
    return EditorState.createWithContent(convertFromRaw(content));
  }
  // Plain string fallback (legacy data)
  return EditorState.createWithContent(
    ContentState.createFromText(String(content))
  );
}

const INLINE_STYLES = [
  { label: "B", style: "BOLD", fontWeight: "bold" },
  { label: "I", style: "ITALIC", fontStyle: "italic" },
  { label: "U", style: "UNDERLINE", textDecoration: "underline" }
];

class ReceiptRichTextEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editorState: createEditorStateFromContent(props.content)
    };
    this.onChange = this.onChange.bind(this);
    this.handleKeyCommand = this.handleKeyCommand.bind(this);
  }

  onChange(editorState) {
    this.setState({ editorState });
    const raw = convertToRaw(editorState.getCurrentContent());
    this.props.onContentChange(raw);
  }

  handleKeyCommand(command) {
    const newState = RichUtils.handleKeyCommand(
      this.state.editorState,
      command
    );
    if (newState) {
      this.onChange(newState);
      return "handled";
    }
    return "not-handled";
  }

  toggleInlineStyle(style) {
    this.onChange(
      RichUtils.toggleInlineStyle(this.state.editorState, style)
    );
  }

  render() {
    const { editorState } = this.state;
    const currentStyle = editorState.getCurrentInlineStyle();

    return (
      <div className="ReceiptRichText-editor">
        <div className="ReceiptRichText-toolbar">
          {INLINE_STYLES.map((type) => (
            <button
              key={type.style}
              type="button"
              className={
                "ReceiptRichText-btn" +
                (currentStyle.has(type.style)
                  ? " ReceiptRichText-btn--active"
                  : "")
              }
              onMouseDown={(e) => {
                e.preventDefault();
                this.toggleInlineStyle(type.style);
              }}
              style={{
                fontWeight: type.fontWeight || "normal",
                fontStyle: type.fontStyle || "normal",
                textDecoration: type.textDecoration || "none"
              }}
            >
              {type.label}
            </button>
          ))}
        </div>
        <div className="ReceiptRichText-body">
          <Editor
            editorState={editorState}
            onChange={this.onChange}
            handleKeyCommand={this.handleKeyCommand}
            placeholder="Enter text..."
          />
        </div>
      </div>
    );
  }
}

ReceiptRichTextEditor.propTypes = {
  content: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onContentChange: PropTypes.func.isRequired
};

export default ReceiptRichTextEditor;
